import React, { useState, useEffect, useRef } from 'react';
import { Calendar } from './components/ui/calendar';
import { Button } from './components/ui/button';
import { Card, CardHeader, CardContent, CardTitle } from './components/ui/card';
import { Input } from './components/ui/input';
import { Label } from './components/ui/label';
import { Textarea } from './components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './components/ui/select';
import { Badge } from './components/ui/badge';
import { Alert, AlertDescription } from './components/ui/alert';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './components/ui/dialog';
import { toast, Toaster } from 'sonner';
import { format, startOfWeek, addDays, isSameDay, parseISO } from 'date-fns';
import { da } from 'date-fns/locale';
import { Plus, ChevronLeft, ChevronRight, LogOut, Settings } from 'lucide-react';
import axios from 'axios';
import { AuthProvider, useAuth } from './AuthContext';
import { LoginForm } from './components/LoginForm';
import { AdminPanel } from './components/AdminPanel';
import { AcceptInviteForm } from './components/AcceptInviteForm';
import './App.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BUILDING_GROUPS = {
  'Færøvej 60': ['Fodbolden', 'Trampolinen', 'Rocken'],
  'Færøvej 54': ['Senior lokalet', 'Junior lokalet', 'Mødelokale i kælder', 'Taktik / video lokalet i kælder']
};

const ALL_RESOURCES = [
  'Fodbolden', 
  'Trampolinen', 
  'Rocken',
  'Senior lokalet',
  'Junior lokalet',
  'Mødelokale i kælder',
  'Taktik / video lokalet i kælder'
];

const RESOURCES = ALL_RESOURCES; // Keep for backwards compatibility

const RESOURCE_INFO = {
  'Fodbolden': {
    capacity: '6 personer',
    type: 'Lille mødelokale',
    building: 'Færøvej 60'
  },
  'Trampolinen': {
    capacity: '8 personer', 
    type: 'Store mødelokale',
    building: 'Færøvej 60'
  },
  'Rocken': {
    capacity: '2 kontor pladser',
    type: '1. salen',
    building: 'Færøvej 60'
  },
  'Senior lokalet': {
    capacity: '40-60 personer',
    type: 'Mulighed for projektor / skærm',
    building: 'Færøvej 54'
  },
  'Junior lokalet': {
    capacity: '10-20 personer',
    type: 'Ingen mulighed for skærm',
    building: 'Færøvej 54'
  },
  'Mødelokale i kælder': {
    capacity: '8-10 personer',
    type: 'Mulighed for skærm',
    building: 'Færøvej 54'
  },
  'Taktik / video lokalet i kælder': {
    capacity: '20 personer',
    type: 'Mulighed for skærm',
    building: 'Færøvej 54'
  }
};

function CalendarApp() {
  const { user, logout, isAdmin } = useAuth();
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  
  // Booking form state
  const [formData, setFormData] = useState({
    resource: '',
    date: format(new Date(), 'yyyy-MM-dd'),
    start_time: '',
    end_time: '',
    title: '',
    notes: ''
  });
  const [duration, setDuration] = useState('1t');
  const [showCustomEndTime, setShowCustomEndTime] = useState(false);
  const [showTimePicker, setShowTimePicker] = useState(false);
  const [showEndTimePicker, setShowEndTimePicker] = useState(false);
  const [isRecurring, setIsRecurring] = useState(false);
  const [recurringType, setRecurringType] = useState('weekly');
  const [weekOffset, setWeekOffset] = useState(0);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [showBookingDetails, setShowBookingDetails] = useState(false);
  const timePickerRef = useRef(null);
  const endTimePickerRef = useRef(null);
  const [dateInputEnabled, setDateInputEnabled] = useState(false);
  const [expandedCells, setExpandedCells] = useState({});
  
  const toggleCellExpand = (key, e) => {
    e.stopPropagation();
    setExpandedCells(prev => ({ ...prev, [key]: !prev[key] }));
  };
  
  // Logo settings state
  const [logoSettings, setLogoSettings] = useState({
    gymnastik_size: 60,
    mgbk_size: 64,
    rub_size: 64
  });

  // Fetch logo settings
  useEffect(() => {
    const fetchLogoSettings = async () => {
      try {
        const response = await axios.get(`${API}/logo-settings`);
        setLogoSettings(response.data);
      } catch (error) {
        console.log('Using default logo settings');
      }
    };
    fetchLogoSettings();
  }, []);

  // Fetch all bookings
  const fetchBookings = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/bookings`);
      setBookings(response.data);
    } catch (error) {
      console.error('Error fetching bookings:', error);
      toast.error('Kunne ikke indlæse bookinger');
    } finally {
      setLoading(false);
    }
  };

  // This function is no longer needed since we fetch all bookings
  // const fetchWeekBookings = async (startDate) => {
  //   // Removed - we now keep all bookings in state
  // };

  useEffect(() => {
    fetchBookings();
  }, []);

  // No need to fetch bookings when week changes since we have all bookings
  // useEffect(() => {
  //   const weekDates = getCurrentWeekDates();
  //   fetchWeekBookings(weekDates[0]);
  // }, [weekOffset]);

  // Close time pickers when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (timePickerRef.current && !timePickerRef.current.contains(event.target)) {
        setShowTimePicker(false);
      }
      if (endTimePickerRef.current && !endTimePickerRef.current.contains(event.target)) {
        setShowEndTimePicker(false);
      }
    };

    if (showTimePicker || showEndTimePicker) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showTimePicker, showEndTimePicker]);

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!formData.resource || !formData.date || !formData.start_time || !formData.end_time || !formData.title) {
      setError('Udfyld venligst alle påkrævede felter');
      return;
    }

    try {
      setLoading(true);
      
      if (isRecurring) {
        // Create recurring bookings
        await axios.post(`${API}/bookings/recurring`, {
          ...formData,
          recurring_type: recurringType
        });
        toast.success(`Tilbagevendende booking oprettet med succes! (${recurringType === 'weekly' ? 'Ugentligt' : 'Månedligt'})`);
      } else {
        // Create single booking
        await axios.post(`${API}/bookings`, formData);
        toast.success('Booking oprettet med succes!');
      }
      
      // Reset form
      setFormData({
        resource: '',
        date: format(new Date(), 'yyyy-MM-dd'),
        start_time: '',
        end_time: '',
        title: '',
        notes: ''
      });
      setDuration('1t');
      setShowCustomEndTime(false);
      setShowTimePicker(false);
      setShowEndTimePicker(false);
      setIsRecurring(false);
      setRecurringType('weekly');
      
      // Close dialog and refresh bookings
      setIsDialogOpen(false);
      fetchBookings(); // Fetch all bookings
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Kunne ikke oprette booking';
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Calculate end time based on start time and duration
  const calculateEndTime = (startTime, durationValue) => {
    if (!startTime) return '';
    
    const [hours, minutes] = startTime.split(':').map(Number);
    const startDate = new Date();
    startDate.setHours(hours, minutes, 0, 0);
    
    let durationMinutes = 0;
    switch(durationValue) {
      case '30m': durationMinutes = 30; break;
      case '1t': durationMinutes = 60; break;
      case '2t': durationMinutes = 120; break;
      default: return formData.end_time; // For custom
    }
    
    const endDate = new Date(startDate.getTime() + durationMinutes * 60000);
    return `${endDate.getHours().toString().padStart(2, '0')}:${endDate.getMinutes().toString().padStart(2, '0')}`;
  };

  // Handle duration change
  const handleDurationChange = (newDuration) => {
    setDuration(newDuration);
    setShowCustomEndTime(newDuration === 'custom');
    
    if (newDuration !== 'custom' && formData.start_time) {
      const calculatedEndTime = calculateEndTime(formData.start_time, newDuration);
      setFormData(prev => ({ ...prev, end_time: calculatedEndTime }));
    }
  };

  // Handle start time change
  const handleStartTimeChange = (newStartTime) => {
    setFormData(prev => ({ ...prev, start_time: newStartTime }));
    
    if (duration !== 'custom') {
      const calculatedEndTime = calculateEndTime(newStartTime, duration);
      setFormData(prev => ({ ...prev, end_time: calculatedEndTime }));
    }
  };

  // Generate time slots in 15-minute intervals
  const generateTimeSlots = () => {
    const slots = [];
    for (let hour = 7; hour <= 22; hour++) {
      for (let minute = 0; minute < 60; minute += 15) {
        const timeString = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
        slots.push(timeString);
      }
    }
    return slots;
  };

  // Generate end time slots (only times after start time)
  const generateEndTimeSlots = () => {
    if (!formData.start_time) return [];
    
    const allSlots = generateTimeSlots();
    const startTimeIndex = allSlots.indexOf(formData.start_time);
    
    if (startTimeIndex === -1) return allSlots;
    
    // Return only slots after the start time (minimum 15 minutes later)
    return allSlots.slice(startTimeIndex + 1);
  };

  // Handle time slot selection
  const handleTimeSlotSelect = (time) => {
    handleStartTimeChange(time);
    setShowTimePicker(false);
  };

  // Handle end time slot selection
  const handleEndTimeSlotSelect = (time) => {
    setFormData(prev => ({ ...prev, end_time: time }));
    setShowEndTimePicker(false);
  };

  // Get bookings for a specific date and resource
  const getBookingsForDateAndResource = (date, resource) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return bookings.filter(booking => 
      booking.date === dateStr && booking.resource === resource
    );
  };

  // Get all bookings for a specific date
  const getBookingsForDate = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return bookings.filter(booking => booking.date === dateStr);
  };

  // Generate week dates
  const getWeekDates = (date) => {
    const start = startOfWeek(date, { weekStartsOn: 1 }); // Monday start
    return Array.from({ length: 7 }, (_, i) => addDays(start, i));
  };

  // Get current week dates with offset
  const getCurrentWeekDates = () => {
    const baseDate = addDays(selectedDate, weekOffset * 7);
    return getWeekDates(baseDate);
  };

  // Navigate weeks
  const navigateWeek = (direction) => {
    setWeekOffset(prev => prev + direction);
  };

  // Handle calendar box click to pre-fill booking form
  const handleCalendarBoxClick = (resource, date) => {
    // Check organization-based room permissions
    if (resource === "Rocken" && user?.organization !== "Rock Under Broen") {
      toast.error("Kun brugere fra 'Rock Under Broen' kan booke dette lokale");
      return;
    }
    
    // Pre-fill the form with selected resource and date
    setFormData(prev => ({
      ...prev,
      resource: resource,
      date: format(date, 'yyyy-MM-dd')
    }));
    
    // Reset other form states
    setDuration('1t');
    setShowCustomEndTime(false);
    setShowTimePicker(false);
    setShowEndTimePicker(false);
    setIsRecurring(false);
    setRecurringType('weekly');
    setError('');
    
    // Open the booking modal
    setIsDialogOpen(true);
  };

  // Handle booking click to show details
  const handleBookingClick = (booking) => {
    setSelectedBooking(booking);
    setShowBookingDetails(true);
  };

  // Close booking details modal
  const closeBookingDetails = () => {
    setShowBookingDetails(false);
    setSelectedBooking(null);
  };

  // Resource color mapping with gradient styles
  const resourceColors = {
    'Fodbolden': 'bg-gradient-to-br from-emerald-50 to-emerald-100 text-emerald-900 border-emerald-200 shadow-emerald-100',
    'Trampolinen': 'bg-gradient-to-br from-blue-50 to-blue-100 text-blue-900 border-blue-200 shadow-blue-100',
    'Rocken': 'bg-gradient-to-br from-purple-50 to-purple-100 text-purple-900 border-purple-200 shadow-purple-100',
    'Senior lokalet': 'bg-gradient-to-br from-orange-50 to-orange-100 text-orange-900 border-orange-200 shadow-orange-100',
    'Junior lokalet': 'bg-gradient-to-br from-teal-50 to-teal-100 text-teal-900 border-teal-200 shadow-teal-100',
    'Mødelokale i kælder': 'bg-gradient-to-br from-indigo-50 to-indigo-100 text-indigo-900 border-indigo-200 shadow-indigo-100',
    'Taktik / video lokalet i kælder': 'bg-gradient-to-br from-rose-50 to-rose-100 text-rose-900 border-rose-200 shadow-rose-100'
  };

  // Helper function to get first name only
  const getFirstName = (fullName) => {
    if (!fullName) return '';
    return fullName.split(' ')[0];
  };

  // Helper function to get resource color classes
  const getResourceColorClasses = (resource) => {
    const colorMap = {
      'Fodbolden': 'bg-gradient-to-br from-emerald-50 via-emerald-30 to-emerald-100 border-emerald-300 text-emerald-900 shadow-emerald-500/10',
      'Trampolinen': 'bg-gradient-to-br from-blue-50 via-blue-30 to-blue-100 border-blue-300 text-blue-900 shadow-blue-500/10',
      'Rocken': 'bg-gradient-to-br from-purple-50 via-purple-30 to-purple-100 border-purple-300 text-purple-900 shadow-purple-500/10',
      'Senior lokalet': 'bg-gradient-to-br from-orange-50 via-orange-30 to-orange-100 border-orange-300 text-orange-900 shadow-orange-500/10',
      'Junior lokalet': 'bg-gradient-to-br from-teal-50 via-teal-30 to-teal-100 border-teal-300 text-teal-900 shadow-teal-500/10',
      'Mødelokale i kælder': 'bg-gradient-to-br from-indigo-50 via-indigo-30 to-indigo-100 border-indigo-300 text-indigo-900 shadow-indigo-500/10',
      'Taktik / video lokalet i kælder': 'bg-gradient-to-br from-rose-50 via-rose-30 to-rose-100 border-rose-300 text-rose-900 shadow-rose-500/10'
    };
    return colorMap[resource] || 'bg-gradient-to-br from-slate-50 to-slate-100 border-slate-300 text-slate-900';
  };

  // Helper function to check if user can access a resource
  const canAccessResource = (resource) => {
    if (resource === 'Rocken') {
      return user?.team === 'Rock Under Broen';
    }
    const faerovej54Rooms = ['Senior lokalet', 'Junior lokalet', 'Mødelokale i kælder', 'Taktik / video lokalet i kælder'];
    if (faerovej54Rooms.includes(resource)) {
      return user?.team === 'MG&BK Fodbold' || user?.team === 'MG&BK Gymnastik';
    }
    return true; // Fodbolden and Trampolinen accessible to all
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-cyan-50 w-full">
      <Toaster position="top-right" richColors />
      
      {/* Header */}
      <div className="w-full px-4 md:px-6 pt-4">
        <div className="bg-white/80 backdrop-blur-md border-b border-indigo-100 shadow-lg shadow-indigo-500/10 rounded-lg px-4 md:px-6 py-4 navbar-spacing">
          <div className="container-huge flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="flex items-center gap-2 md:gap-4 lg:gap-8 flex-wrap justify-center md:justify-start">
            <img 
              src="https://customer-assets.emergentagent.com/job_roomreserve-46/artifacts/ogw5o67w_cropped-Logo-med-hvid-baggrund-90x90.png" 
              alt="Gymnastik Logo" 
              style={{ height: `${logoSettings.gymnastik_size}px`, width: `${logoSettings.gymnastik_size}px` }}
              className="object-contain"
            />
            <img 
              src="https://customer-assets.emergentagent.com/job_roomreserve-46/artifacts/pzd8lr16_MG_%26_BK_logo_136.png" 
              alt="MG & BK Logo" 
              style={{ height: `${logoSettings.mgbk_size}px` }}
              className="object-contain"
            />
            <img 
              src="https://customer-assets.emergentagent.com/job_roomreserve-46/artifacts/h1gpmvyf_RUB_logo_Siden_1989_CMYK.jpg" 
              alt="Rock Under Broen Logo" 
              style={{ height: `${logoSettings.rub_size}px` }}
              className="object-contain"
            />
          </div>
          
          <div className="flex flex-col md:flex-row items-center gap-2 md:gap-4 w-full md:w-auto">
            {/* User Info */}
            <div className="text-xs md:text-sm text-indigo-700 text-center md:text-left">
              <div>Velkommen, <span className="font-semibold text-indigo-900">{user?.name || user?.email}</span></div>
              <div className="flex items-center justify-center md:justify-start gap-2 mt-1">
                {user?.organization && <span className="text-xs text-indigo-600">{user.organization}</span>}
                {isAdmin() && <Badge className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-md text-xs">Admin</Badge>}
              </div>
            </div>
            
            <div className="flex items-center gap-2">
            
            {/* Admin Panel Button */}
            {isAdmin() && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowAdminPanel(!showAdminPanel)}
                className={`transition-all duration-200 text-xs md:text-sm ${
                  showAdminPanel 
                    ? 'bg-gradient-to-r from-indigo-100 to-purple-100 border-indigo-300 text-indigo-700 shadow-md' 
                    : 'border-indigo-200 text-indigo-700 hover:bg-indigo-50'
                }`}
              >
                <Settings className="w-3 h-3 md:w-4 md:h-4 mr-1 md:mr-2" />
                <span className="hidden sm:inline">Admin</span>
              </Button>
            )}
            
            {/* Logout Button */}
            <Button
              variant="outline"
              size="sm"
              onClick={logout}
              className="text-red-600 border-red-200 hover:bg-red-50 hover:border-red-300 transition-all duration-200 text-xs md:text-sm"
            >
              <LogOut className="w-3 h-3 md:w-4 md:h-4 mr-1 md:mr-2" />
              <span className="hidden sm:inline">Log ud</span>
            </Button>
            </div>
            
            {/* New Booking Button */}
          <Dialog open={isDialogOpen} onOpenChange={(open) => {
            setIsDialogOpen(open);
            if (!open) {
              // Reset date input enabled state when dialog closes
              setDateInputEnabled(false);
            }
          }}>
            <DialogTrigger asChild>
              <Button 
                className="bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-700 hover:to-blue-700 text-white shadow-xl shadow-indigo-500/25 hover:shadow-2xl hover:shadow-indigo-500/30 transition-all duration-300 border-0 w-full md:w-auto"
                size="lg"
                data-testid="new-booking-btn"
              >
                <Plus className="w-4 h-4 md:w-5 md:h-5 mr-2" />
                Ny Booking
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl mx-2 md:mx-auto bg-gradient-to-br from-white to-indigo-50 border-0 shadow-2xl shadow-indigo-500/20 p-0 flex flex-col h-[calc(100vh-100px)] overflow-x-hidden">
              <DialogHeader className="bg-gradient-to-r from-indigo-600 to-purple-600 p-6 rounded-t-lg">
                <DialogTitle className="text-2xl font-bold text-white">Opret Ny Booking</DialogTitle>
              </DialogHeader>
              <div className="overflow-y-auto overflow-x-hidden flex-1">
                <form id="booking-form" onSubmit={handleSubmit} className="space-y-4 px-6 py-4 max-w-full">
                {error && (
                  <Alert className="border-red-200 bg-red-50" data-testid="error-message">
                    <AlertDescription className="text-red-800">{error}</AlertDescription>
                  </Alert>
                )}
                
                <div className="space-y-4">
                  <div className="space-y-3">
                    <Label>Vælg Mødelokale *</Label>
                    
                    {/* Færøvej 60 Building */}
                    <div className="mb-6">
                      <h3 className="text-lg font-bold text-gray-800 mb-3 pb-2 border-b-2 border-gray-200">📍 Færøvej 60</h3>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {/* Fodbolden */}
                        <div 
                          className={`p-6 border-2 rounded-xl cursor-pointer transition-all duration-300 shadow-lg ${
                            formData.resource === 'Fodbolden' 
                              ? 'border-emerald-500 bg-gradient-to-br from-emerald-50 to-emerald-100 shadow-xl shadow-emerald-500/20 scale-105' 
                              : 'border-emerald-200 bg-gradient-to-br from-emerald-50 to-white hover:border-emerald-300 hover:shadow-xl hover:scale-102 hover:shadow-emerald-500/10'
                          }`}
                          onClick={() => handleInputChange('resource', 'Fodbolden')}
                          data-testid="resource-fodbolden-box"
                        >
                          <div className="text-center">
                            <h3 className="font-bold text-xl text-emerald-900 mb-3">Fodbolden</h3>
                            <div className="space-y-1 text-emerald-700">
                              <p className="font-medium">6 personer</p>
                              <p className="text-sm">Lille mødelokale</p>
                            </div>
                          </div>
                        </div>

                        {/* Trampolinen */}
                        <div 
                          className={`p-6 border-2 rounded-xl cursor-pointer transition-all duration-300 shadow-lg ${
                            formData.resource === 'Trampolinen' 
                              ? 'border-blue-500 bg-gradient-to-br from-blue-50 to-blue-100 shadow-xl shadow-blue-500/20 scale-105' 
                              : 'border-blue-200 bg-gradient-to-br from-blue-50 to-white hover:border-blue-300 hover:shadow-xl hover:scale-102 hover:shadow-blue-500/10'
                          }`}
                          onClick={() => handleInputChange('resource', 'Trampolinen')}
                          data-testid="resource-trampolinen-box"
                        >
                          <div className="text-center">
                            <h3 className="font-bold text-xl text-blue-900 mb-3">Trampolinen</h3>
                            <div className="space-y-1 text-blue-700">
                              <p className="font-medium">8 personer</p>
                              <p className="text-sm">Store mødelokale</p>
                            </div>
                          </div>
                        </div>

                        {/* Rocken */}
                        <div 
                          className={`p-6 border-2 rounded-xl transition-all duration-300 shadow-lg ${
                            user?.team !== "Rock Under Broen" 
                              ? 'border-slate-300 bg-gradient-to-br from-slate-100 to-slate-200 cursor-not-allowed opacity-50' 
                              : formData.resource === 'Rocken' 
                                ? 'border-purple-500 bg-gradient-to-br from-purple-50 to-purple-100 shadow-xl shadow-purple-500/20 scale-105 cursor-pointer' 
                                : 'border-purple-200 bg-gradient-to-br from-purple-50 to-white hover:border-purple-300 hover:shadow-xl hover:scale-102 hover:shadow-purple-500/10 cursor-pointer'
                          }`}
                          onClick={() => {
                            if (user?.team === "Rock Under Broen") {
                              handleInputChange('resource', 'Rocken');
                            }
                          }}
                          data-testid="resource-rocken-box"
                        >
                          <div className="text-center">
                            <h3 className={`font-bold text-xl mb-3 ${
                              user?.team !== "Rock Under Broen" ? 'text-slate-500' : 'text-purple-900'
                            }`}>Rocken</h3>
                            <div className={`space-y-1 ${
                              user?.team !== "Rock Under Broen" ? 'text-slate-500' : 'text-purple-700'
                            }`}>
                              <p className="font-medium">2 kontor pladser</p>
                              <p className="text-sm">1. salen</p>
                              {user?.team !== "Rock Under Broen" && (
                                <p className="text-xs mt-2 font-semibold">Kun Rock Under Broen</p>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Færøvej 54 Building */}
                    <div>
                      <h3 className="text-lg font-bold text-gray-800 mb-3 pb-2 border-b-2 border-gray-200">📍 Færøvej 54</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Senior lokalet */}
                        <div 
                          className={`p-6 border-2 rounded-xl transition-all duration-300 shadow-lg ${
                            user?.team !== "MG&BK Fodbold" && user?.team !== "MG&BK Gymnastik"
                              ? 'border-slate-300 bg-gradient-to-br from-slate-100 to-slate-200 cursor-not-allowed opacity-50' 
                              : formData.resource === 'Senior lokalet' 
                                ? 'border-orange-500 bg-gradient-to-br from-orange-50 to-orange-100 shadow-xl shadow-orange-500/20 scale-105 cursor-pointer' 
                                : 'border-orange-200 bg-gradient-to-br from-orange-50 to-white hover:border-orange-300 hover:shadow-xl hover:scale-102 hover:shadow-orange-500/10 cursor-pointer'
                          }`}
                          onClick={() => {
                            if (user?.team === "MG&BK Fodbold" || user?.team === "MG&BK Gymnastik") {
                              handleInputChange('resource', 'Senior lokalet');
                            }
                          }}
                        >
                          <div className="text-center">
                            <h3 className={`font-bold text-lg mb-2 ${
                              user?.team !== "MG&BK Fodbold" && user?.team !== "MG&BK Gymnastik" ? 'text-slate-500' : 'text-orange-900'
                            }`}>Senior lokalet</h3>
                            <div className={`space-y-1 text-sm ${
                              user?.team !== "MG&BK Fodbold" && user?.team !== "MG&BK Gymnastik" ? 'text-slate-500' : 'text-orange-700'
                            }`}>
                              <p className="font-medium">40-60 personer</p>
                              <p>Projektor / skærm</p>
                              {user?.team !== "MG&BK Fodbold" && user?.team !== "MG&BK Gymnastik" && (
                                <p className="text-xs mt-2 font-semibold">Kun MG&BK</p>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Junior lokalet */}
                        <div 
                          className={`p-6 border-2 rounded-xl transition-all duration-300 shadow-lg ${
                            user?.team !== "MG&BK Fodbold" && user?.team !== "MG&BK Gymnastik"
                              ? 'border-slate-300 bg-gradient-to-br from-slate-100 to-slate-200 cursor-not-allowed opacity-50' 
                              : formData.resource === 'Junior lokalet' 
                                ? 'border-teal-500 bg-gradient-to-br from-teal-50 to-teal-100 shadow-xl shadow-teal-500/20 scale-105 cursor-pointer' 
                                : 'border-teal-200 bg-gradient-to-br from-teal-50 to-white hover:border-teal-300 hover:shadow-xl hover:scale-102 hover:shadow-teal-500/10 cursor-pointer'
                          }`}
                          onClick={() => {
                            if (user?.team === "MG&BK Fodbold" || user?.team === "MG&BK Gymnastik") {
                              handleInputChange('resource', 'Junior lokalet');
                            }
                          }}
                        >
                          <div className="text-center">
                            <h3 className={`font-bold text-lg mb-2 ${
                              user?.team !== "MG&BK Fodbold" && user?.team !== "MG&BK Gymnastik" ? 'text-slate-500' : 'text-teal-900'
                            }`}>Junior lokalet</h3>
                            <div className={`space-y-1 text-sm ${
                              user?.team !== "MG&BK Fodbold" && user?.team !== "MG&BK Gymnastik" ? 'text-slate-500' : 'text-teal-700'
                            }`}>
                              <p className="font-medium">10-20 personer</p>
                              <p>Ingen skærm</p>
                              {user?.team !== "MG&BK Fodbold" && user?.team !== "MG&BK Gymnastik" && (
                                <p className="text-xs mt-2 font-semibold">Kun MG&BK</p>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Mødelokale i kælder */}
                        <div 
                          className={`p-6 border-2 rounded-xl transition-all duration-300 shadow-lg ${
                            user?.team !== "MG&BK Fodbold" && user?.team !== "MG&BK Gymnastik"
                              ? 'border-slate-300 bg-gradient-to-br from-slate-100 to-slate-200 cursor-not-allowed opacity-50' 
                              : formData.resource === 'Mødelokale i kælder' 
                                ? 'border-indigo-500 bg-gradient-to-br from-indigo-50 to-indigo-100 shadow-xl shadow-indigo-500/20 scale-105 cursor-pointer' 
                                : 'border-indigo-200 bg-gradient-to-br from-indigo-50 to-white hover:border-indigo-300 hover:shadow-xl hover:scale-102 hover:shadow-indigo-500/10 cursor-pointer'
                          }`}
                          onClick={() => {
                            if (user?.team === "MG&BK Fodbold" || user?.team === "MG&BK Gymnastik") {
                              handleInputChange('resource', 'Mødelokale i kælder');
                            }
                          }}
                        >
                          <div className="text-center">
                            <h3 className={`font-bold text-lg mb-2 ${
                              user?.team !== "MG&BK Fodbold" && user?.team !== "MG&BK Gymnastik" ? 'text-slate-500' : 'text-indigo-900'
                            }`}>Mødelokale i kælder</h3>
                            <div className={`space-y-1 text-sm ${
                              user?.team !== "MG&BK Fodbold" && user?.team !== "MG&BK Gymnastik" ? 'text-slate-500' : 'text-indigo-700'
                            }`}>
                              <p className="font-medium">8-10 personer</p>
                              <p>Mulighed for skærm</p>
                              {user?.team !== "MG&BK Fodbold" && user?.team !== "MG&BK Gymnastik" && (
                                <p className="text-xs mt-2 font-semibold">Kun MG&BK</p>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Taktik / video lokalet */}
                        <div 
                          className={`p-6 border-2 rounded-xl transition-all duration-300 shadow-lg ${
                            user?.team !== "MG&BK Fodbold" && user?.team !== "MG&BK Gymnastik"
                              ? 'border-slate-300 bg-gradient-to-br from-slate-100 to-slate-200 cursor-not-allowed opacity-50' 
                              : formData.resource === 'Taktik / video lokalet i kælder' 
                                ? 'border-rose-500 bg-gradient-to-br from-rose-50 to-rose-100 shadow-xl shadow-rose-500/20 scale-105 cursor-pointer' 
                                : 'border-rose-200 bg-gradient-to-br from-rose-50 to-white hover:border-rose-300 hover:shadow-xl hover:scale-102 hover:shadow-rose-500/10 cursor-pointer'
                          }`}
                          onClick={() => {
                            if (user?.team === "MG&BK Fodbold" || user?.team === "MG&BK Gymnastik") {
                              handleInputChange('resource', 'Taktik / video lokalet i kælder');
                            }
                          }}
                        >
                          <div className="text-center">
                            <h3 className={`font-bold text-lg mb-2 ${
                              user?.team !== "MG&BK Fodbold" && user?.team !== "MG&BK Gymnastik" ? 'text-slate-500' : 'text-rose-900'
                            }`}>Taktik / video lokalet</h3>
                            <div className={`space-y-1 text-sm ${
                              user?.team !== "MG&BK Fodbold" && user?.team !== "MG&BK Gymnastik" ? 'text-slate-500' : 'text-rose-700'
                            }`}>
                              <p className="font-medium">20 personer</p>
                              <p>Mulighed for skærm</p>
                              {user?.team !== "MG&BK Fodbold" && user?.team !== "MG&BK Gymnastik" && (
                                <p className="text-xs mt-2 font-semibold">Kun MG&BK</p>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="date">Dato *</Label>
                    <Input
                      id="date"
                      type="date"
                      value={formData.date}
                      onChange={(e) => handleInputChange('date', e.target.value)}
                      data-testid="date-input"
                      required
                      disabled={!dateInputEnabled}
                      onTouchStart={() => {
                        if (!dateInputEnabled) {
                          setDateInputEnabled(true);
                        }
                      }}
                      onClick={() => {
                        if (!dateInputEnabled) {
                          setDateInputEnabled(true);
                        }
                      }}
                    />
                  </div>
                </div>
                
                <div className="space-y-4">
                  <div className="space-y-2 relative" ref={timePickerRef}>
                    <Label>Start Tid *</Label>
                    <Button
                      type="button"
                      variant="outline"
                      className="w-full justify-start text-left h-10 px-3 py-2 border border-input bg-background"
                      onClick={() => setShowTimePicker(!showTimePicker)}
                      data-testid="start-time-picker"
                    >
                      {formData.start_time || 'Vælg tid'}
                    </Button>
                    
                    {showTimePicker && (
                      <Card className="absolute top-full left-0 right-0 z-50 mt-1 p-4 shadow-xl border">
                        <div className="mb-2 text-sm font-medium text-slate-600">Vælg tidspunkt</div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-1 max-h-64 overflow-y-auto">
                          {generateTimeSlots().map(time => (
                            <Button
                              key={time}
                              type="button"
                              variant={formData.start_time === time ? 'default' : 'ghost'}
                              size="sm"
                              className={`text-xs h-8 ${
                                formData.start_time === time 
                                  ? 'bg-blue-600 text-white hover:bg-blue-700' 
                                  : 'hover:bg-slate-100'
                              }`}
                              onClick={() => handleTimeSlotSelect(time)}
                              data-testid={`time-slot-${time}`}
                            >
                              {time}
                            </Button>
                          ))}
                        </div>
                      </Card>
                    )}
                  </div>
                  
                  <div className="space-y-3">
                    <Label>Varighed *</Label>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                      <Button
                        type="button"
                        variant={duration === '30m' ? 'default' : 'outline'}
                        className={`${duration === '30m' ? 'bg-blue-600 text-white' : ''}`}
                        onClick={() => handleDurationChange('30m')}
                        data-testid="duration-30m"
                      >
                        30m
                      </Button>
                      <Button
                        type="button"
                        variant={duration === '1t' ? 'default' : 'outline'}
                        className={`${duration === '1t' ? 'bg-blue-600 text-white' : ''}`}
                        onClick={() => handleDurationChange('1t')}
                        data-testid="duration-1t"
                      >
                        1t
                      </Button>
                      <Button
                        type="button"
                        variant={duration === '2t' ? 'default' : 'outline'}
                        className={`${duration === '2t' ? 'bg-blue-600 text-white' : ''}`}
                        onClick={() => handleDurationChange('2t')}
                        data-testid="duration-2t"
                      >
                        2t
                      </Button>
                      <Button
                        type="button"
                        variant={duration === 'custom' ? 'default' : 'outline'}
                        className={`${duration === 'custom' ? 'bg-blue-600 text-white' : ''}`}
                        onClick={() => handleDurationChange('custom')}
                        data-testid="duration-custom"
                      >
                        Custom
                      </Button>
                    </div>
                  </div>
                  
                  {showCustomEndTime && (
                    <div className="space-y-2 relative" ref={endTimePickerRef}>
                      <Label>Slut Tid *</Label>
                      <Button
                        type="button"
                        variant="outline"
                        className="w-full justify-start text-left h-10 px-3 py-2 border border-input bg-background"
                        onClick={() => setShowEndTimePicker(!showEndTimePicker)}
                        data-testid="end-time-picker"
                      >
                        {formData.end_time || 'Vælg slut tid'}
                      </Button>
                      
                      {showEndTimePicker && (
                        <Card className="absolute top-full left-0 right-0 z-50 mt-1 p-4 shadow-xl border">
                          <div className="mb-2 text-sm font-medium text-slate-600">Vælg slut tidspunkt</div>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-1 max-h-64 overflow-y-auto">
                            {generateEndTimeSlots().map(time => (
                              <Button
                                key={time}
                                type="button"
                                variant={formData.end_time === time ? 'default' : 'ghost'}
                                size="sm"
                                className={`text-xs h-8 ${
                                  formData.end_time === time 
                                    ? 'bg-blue-600 text-white hover:bg-blue-700' 
                                    : 'hover:bg-slate-100'
                                }`}
                                onClick={() => handleEndTimeSlotSelect(time)}
                                data-testid={`end-time-slot-${time}`}
                              >
                                {time}
                              </Button>
                            ))}
                          </div>
                        </Card>
                      )}
                    </div>
                  )}
                  
                  {!showCustomEndTime && formData.start_time && (
                    <div className="text-sm text-slate-600">
                      <span className="font-medium">Slut tid:</span> {formData.end_time || 'Vælg start tid'}
                    </div>
                  )}
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="title">Titel *</Label>
                  <Input
                    id="title"
                    value={formData.title}
                    onChange={(e) => handleInputChange('title', e.target.value)}
                    placeholder="Møde titel"
                    data-testid="title-input"
                    required
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="notes">Noter</Label>
                  <Textarea
                    id="notes"
                    value={formData.notes}
                    onChange={(e) => handleInputChange('notes', e.target.value)}
                    placeholder="Yderligere noter (valgfri)"
                    data-testid="notes-input"
                    rows={3}
                  />
                </div>
                
                {/* Recurring Event Options */}
                <div className="space-y-3 pt-4 border-t border-slate-200">
                  <div className="flex items-center space-x-3">
                    <Button
                      type="button"
                      variant={isRecurring ? "default" : "outline"}
                      size="sm"
                      onClick={() => setIsRecurring(!isRecurring)}
                      className={`${isRecurring ? 'bg-purple-600 text-white hover:bg-purple-700' : ''}`}
                      data-testid="recurring-toggle"
                    >
                      📅 Gentagende møde
                    </Button>
                  </div>
                  
                  {isRecurring && (
                    <div className="space-y-2">
                      <Label>Gentagelse</Label>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        <Button
                          type="button"
                          variant={recurringType === 'weekly' ? 'default' : 'outline'}
                          className={`${recurringType === 'weekly' ? 'bg-purple-600 text-white' : ''}`}
                          onClick={() => setRecurringType('weekly')}
                          data-testid="recurring-weekly"
                        >
                          📅 Hver uge
                        </Button>
                        <Button
                          type="button"
                          variant={recurringType === 'monthly' ? 'default' : 'outline'}
                          className={`${recurringType === 'monthly' ? 'bg-purple-600 text-white' : ''}`}
                          onClick={() => setRecurringType('monthly')}
                          data-testid="recurring-monthly"
                        >
                          🗓️ Hver måned
                        </Button>
                      </div>
                      <div className="text-xs text-slate-600">
                        {recurringType === 'weekly' 
                          ? 'Opretter 10 bookinger (10 uger fremad)' 
                          : 'Opretter 6 bookinger (6 måneder fremad)'}
                      </div>
                    </div>
                  )}
                </div>
                
                </form>
              </div>
              <div className="flex gap-4 p-6 border-t border-indigo-200 bg-white">
                <Button 
                  type="button" 
                  variant="outline"
                  onClick={() => setIsDialogOpen(false)}
                  className="flex-1 border-slate-300 text-slate-700 hover:bg-slate-100 hover:border-slate-400 transition-all duration-200"
                >
                  Annuller
                </Button>
                <Button 
                  type="submit"
                  form="booking-form"
                  disabled={loading}
                  className="flex-1 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white shadow-xl shadow-indigo-500/25 hover:shadow-2xl hover:shadow-indigo-500/30 transition-all duration-300 border-0"
                  data-testid="submit-booking-btn"
                >
                  {loading ? 'Opretter...' : 'Opret Booking'}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
          </div>
        </div>
      </div>

      {/* Admin Panel */}
      {isAdmin() && showAdminPanel && (
        <div className="bg-gradient-to-r from-indigo-50 to-purple-50 border-b border-indigo-200 shadow-inner">
          <div className="container-huge py-8 px-4 md:px-6">
            <AdminPanel />
          </div>
        </div>
      )}

      {/* Main Calendar View */}
      <div className="w-full pb-4 md:pb-8 px-4 md:px-6 space-y-4 md:space-y-8 container-huge">
        {/* Day View and Calendar Picker - HIDDEN per user request */}
        {false && <div className="px-2 md:px-4">
          <div className="flex flex-col lg:grid lg:grid-cols-4 gap-4 md:gap-8">
          {/* Day View */}
          <Card className="lg:col-span-3 border-0 shadow-xl shadow-indigo-500/10 bg-white/80 backdrop-blur-sm order-2 lg:order-1">
            <CardHeader className="bg-gradient-to-r from-indigo-600 to-blue-600 text-white rounded-t-lg pb-4 md:pb-6">
              <CardTitle className="text-lg md:text-xl font-bold mb-2">Program for {format(selectedDate, 'EEEE, d. MMMM yyyy', { locale: da })}</CardTitle>
            </CardHeader>
            <CardContent className="p-6 pt-8">
              <div className="space-y-6">
                {RESOURCES.map(resource => {
                  const resourceBookings = getBookingsForDateAndResource(selectedDate, resource);
                  return (
                    <div key={resource} className="space-y-4">
                      <h3 className="font-bold text-lg text-slate-800 border-l-4 border-indigo-500 pl-3">{resource}</h3>
                      <div className="grid gap-3">
                        {resourceBookings.length === 0 ? (
                          <div className="p-6 bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl text-slate-600 text-center border-2 border-dashed border-slate-200 hover:border-slate-300 transition-colors duration-200"
                               data-testid={`no-bookings-${resource.toLowerCase()}`}>
                            Ingen bookinger for denne dag
                          </div>
                        ) : (
                          resourceBookings.map(booking => (
                            <div 
                              key={booking.id} 
                              className={`p-5 rounded-xl border-2 cursor-pointer transition-all duration-300 hover:shadow-xl hover:-translate-y-1 shadow-lg ${resourceColors[resource]}`}
                              onClick={() => handleBookingClick(booking)}
                              data-testid={`booking-${booking.id}`}
                            >
                              <div className="flex justify-between items-start">
                                <div>
                                  <h4 className="font-semibold">{booking.title}</h4>
                                  <p className="text-sm opacity-80">
                                    {booking.start_time} - {booking.end_time}
                                  </p>
                                  {booking.creator_name && (
                                    <p className="text-xs opacity-70 mt-1">
                                      Booket af: {getFirstName(booking.creator_name)}
                                      {booking.creator_organization && ` (${booking.creator_organization})`}
                                    </p>
                                  )}
                                  {booking.notes && (
                                    <p className="text-sm mt-1 opacity-70">{booking.notes}</p>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
          
          {/* Calendar Picker */}
          <Card className="border-0 shadow-xl shadow-indigo-500/10 bg-white/80 backdrop-blur-sm order-1 lg:order-2">
            <CardHeader className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-t-lg pb-6">
              <CardTitle className="text-lg font-bold mb-2">Vælg Dato</CardTitle>
            </CardHeader>
            <CardContent className="p-6 pt-8">
              <Calendar
                mode="single"
                selected={selectedDate}
                onSelect={(date) => {
                  setSelectedDate(date);
                }}
                className="rounded-xl border-0 shadow-md"
                data-testid="calendar-picker"
              />
            </CardContent>
          </Card>
          </div>
        </div>}
        
        {/* Week View */}
        <div className="px-2 md:px-4">
        
        {/* Info box for regular users */}
        {!isAdmin() && (
          <div className="mb-4 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-4 shadow-sm">
            <div className="flex items-start gap-3">
              <svg 
                className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={2} 
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" 
                />
              </svg>
              <div>
                <h3 className="font-semibold text-blue-900 mb-1">💡 Tip: Administrer dine møder</h3>
                <p className="text-sm text-blue-800">
                  Klik på dine egne møder i kalenderen for at se detaljer. Du kan slette dine egne møder ved at åbne dem og klikke på "Slet Møde" knappen.
                </p>
              </div>
            </div>
          </div>
        )}
        
        <Card className="shadow-2xl shadow-indigo-500/20 border-0 bg-gradient-to-br from-white via-indigo-50/30 to-purple-50/30 backdrop-blur-sm">
          <CardHeader className="pb-6 md:pb-8 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-t-lg">
            {/* Desktop Layout - Side by side */}
            <div className="hidden md:flex justify-between items-center">
              <div>
                <CardTitle className="text-2xl font-bold text-white mb-3">
                  Uge Oversigt
                </CardTitle>
                <p className="text-indigo-100 mt-2 text-base">Ugentlig kalender oversigt for alle mødelokaler</p>
              </div>
              <div className="flex items-center gap-3">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigateWeek(-1)}
                  data-testid="prev-week-btn"
                  className="h-10 w-10 p-0 rounded-full shadow-md hover:shadow-lg transition-all duration-200 hover:scale-105 border-slate-300 hover:border-slate-400"
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-4 py-2 rounded-full border border-blue-200 shadow-sm">
                  <span className="text-sm font-semibold text-slate-700">
                    {format(getCurrentWeekDates()[0], 'd. MMM', { locale: da })} - {format(getCurrentWeekDates()[6], 'd. MMM yyyy', { locale: da })}
                  </span>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigateWeek(1)}
                  data-testid="next-week-btn"
                  className="h-10 w-10 p-0 rounded-full shadow-md hover:shadow-lg transition-all duration-200 hover:scale-105 border-slate-300 hover:border-slate-400"
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
            
            {/* Mobile Layout - Stacked with controls below */}
            <div className="block md:hidden space-y-4">
              {/* Heading and subheading on same line */}
              <div className="flex flex-col gap-1">
                <div className="flex items-baseline gap-2 flex-wrap">
                  <CardTitle className="text-xl font-bold text-white">
                    Uge Oversigt
                  </CardTitle>
                  <span className="text-indigo-100 text-sm">- Alle mødelokaler</span>
                </div>
              </div>
              
              {/* Navigation controls below */}
              <div className="flex items-center justify-center gap-3">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigateWeek(-1)}
                  data-testid="prev-week-btn-mobile"
                  className="h-10 w-10 p-0 rounded-full shadow-md hover:shadow-lg transition-all bg-white/90 border-white/50"
                >
                  <ChevronLeft className="w-5 h-5" />
                </Button>
                <div className="bg-white/90 px-4 py-2 rounded-full border border-white/50 shadow-sm flex-1 text-center">
                  <span className="text-sm font-semibold text-slate-700">
                    {format(getCurrentWeekDates()[0], 'd. MMM', { locale: da })} - {format(getCurrentWeekDates()[6], 'd. MMM', { locale: da })}
                  </span>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigateWeek(1)}
                  data-testid="next-week-btn-mobile"
                  className="h-10 w-10 p-0 rounded-full shadow-md hover:shadow-lg transition-all bg-white/90 border-white/50"
                >
                  <ChevronRight className="w-5 h-5" />
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="pt-4 md:pt-8 px-0">
            {/* Mobile Layout - Stack rooms vertically with building groupings */}
            <div className="block md:hidden space-y-4 px-3">
              {Object.entries(BUILDING_GROUPS).map(([building, rooms]) => (
                <div key={building} className="space-y-3">
                  {/* Building header for mobile */}
                  <div className="p-3 bg-gradient-to-r from-slate-100 to-slate-50 rounded-lg border-l-4 border-indigo-500">
                    <h3 className="text-sm font-bold text-slate-800">📍 {building}</h3>
                  </div>
                  
                  {/* Rooms in this building */}
                  {rooms.map((resource) => {
                    const hasAccess = canAccessResource(resource);
                    return (
                      <div key={resource} className="bg-gradient-to-br from-white to-indigo-50/20 rounded-xl p-3 shadow-md border border-indigo-100">
                        <div className={`p-3 rounded-lg mb-3 shadow-sm border ${getResourceColorClasses(resource).replace('via-', 'to-').split(' ').slice(0, 4).join(' ')}`}>
                          <div className="font-bold text-base mb-1">{resource}</div>
                          <div className="text-xs space-y-0.5">
                            <div className="font-semibold opacity-90">{RESOURCE_INFO[resource].capacity}</div>
                            <div className="font-medium opacity-75">{RESOURCE_INFO[resource].type}</div>
                            {!hasAccess && (
                              <div className="text-[10px] font-bold mt-1 opacity-70">🔒 Begrænset adgang</div>
                            )}
                          </div>
                        </div>
                        <div className="grid grid-cols-3 gap-2">
                          {getCurrentWeekDates().map(date => {
                            const dayBookings = getBookingsForDateAndResource(date, resource);
                            const isToday = isSameDay(date, new Date());
                            return (
                              <div key={`mobile-${resource}-${date.toISOString()}`} className="space-y-1.5">
                                <div className={`text-center py-1.5 px-2 rounded-md font-semibold text-xs ${
                                  isToday 
                                    ? 'bg-emerald-500 text-white shadow-sm' 
                                    : 'bg-slate-200 text-slate-700'
                                }`}>
                                  <div className="leading-tight">{format(date, 'EEE', { locale: da })}</div>
                                  <div className="text-[10px] opacity-90 leading-tight">{format(date, 'dd/MM')}</div>
                                </div>
                                <div 
                                  className={`p-2 rounded-md min-h-[60px] border transition-all duration-200 ${
                                    !hasAccess
                                      ? 'bg-slate-50 border-slate-200 opacity-60 cursor-not-allowed'
                                      : isToday 
                                        ? 'bg-emerald-50 border-emerald-300 cursor-pointer hover:shadow-sm' 
                                        : 'bg-white border-slate-200 cursor-pointer hover:shadow-sm hover:border-indigo-300'
                                  }`}
                                  onClick={() => hasAccess && handleCalendarBoxClick(resource, date)}
                                >
                                  {dayBookings.length === 0 ? (
                                    <div className="text-center text-slate-400 text-[10px] py-1">
                                      {!hasAccess ? "🔒 Låst" : "Ledig"}
                                    </div>
                                  ) : (
                                    <div className="space-y-1">
                                      {dayBookings.slice(0, expandedCells[`mobile-${resource}-${format(date, 'yyyy-MM-dd')}`] ? undefined : 2).map(booking => (
                                        <div
                                          key={booking.id}
                                          className={`p-1.5 rounded text-[10px] cursor-pointer leading-tight ${resourceColors[resource]}`}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleBookingClick(booking);
                                          }}
                                        >
                                          <div className="font-semibold truncate">{booking.title}</div>
                                          <div className="opacity-90">{booking.start_time}</div>
                                        </div>
                                      ))}
                                      {dayBookings.length > 2 && (
                                        <div 
                                          className="text-[10px] text-indigo-600 text-center cursor-pointer font-semibold hover:text-indigo-800 transition-colors py-0.5 rounded bg-indigo-50"
                                          onClick={(e) => toggleCellExpand(`mobile-${resource}-${format(date, 'yyyy-MM-dd')}`, e)}
                                          data-testid={`expand-cell-mobile-${resource}-${format(date, 'yyyy-MM-dd')}`}
                                        >
                                          {expandedCells[`mobile-${resource}-${format(date, 'yyyy-MM-dd')}`] 
                                            ? 'Vis mindre' 
                                            : `+${dayBookings.length - 2} mere`}
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
            
            {/* Desktop/Tablet Layout - Grid view */}
            <div className="hidden md:block bg-gradient-to-br from-white to-indigo-50/20 rounded-2xl p-3 md:p-6 shadow-inner overflow-x-auto">
              <div className="grid gap-1 md:gap-2 lg:gap-4 w-full min-w-[800px]" style={{gridTemplateColumns: 'minmax(150px, 1.2fr) repeat(7, minmax(80px, 1fr))'}}>
                {/* Header */}
                <div className="font-bold text-indigo-800 p-3 md:p-5 bg-gradient-to-br from-indigo-100 via-indigo-50 to-purple-50 rounded-2xl shadow-lg border-2 border-indigo-200">
                  <div className="text-base md:text-lg font-bold">Lokaler</div>
                  <div className="text-xs md:text-sm text-indigo-600 mt-1 md:mt-2">Møderum</div>
                </div>
                {getCurrentWeekDates().map(date => {
                  const isToday = isSameDay(date, new Date());
                  return (
                    <div 
                      key={date.toISOString()} 
                      className={`font-semibold p-3 md:p-4 lg:p-5 rounded-2xl text-center shadow-lg border-2 transition-all duration-300 hover:scale-105 min-h-[80px] md:min-h-[100px] ${
                        isToday 
                          ? 'bg-gradient-to-b from-emerald-100 via-emerald-50 to-green-100 border-emerald-300 text-emerald-900 shadow-emerald-500/20 shadow-xl' 
                          : 'bg-gradient-to-b from-slate-50 via-white to-slate-50 border-slate-200 text-slate-700 hover:from-indigo-50 hover:to-purple-50 hover:border-indigo-300'
                      }`}
                    >
                      <div className="text-xs md:text-sm lg:text-base font-bold">{format(date, 'EEE', { locale: da })}</div>
                      <div className="text-xs md:text-sm opacity-75 mt-1 md:mt-2 font-medium">{format(date, 'dd/MM')}</div>
                    </div>
                  );
                })}
                
                {/* Rows for each resource with building groupings */}
                {Object.entries(BUILDING_GROUPS).map(([building, rooms]) => (
                  <React.Fragment key={building}>
                    {/* Building header spanning all columns */}
                    <div className="col-span-8 p-2 md:p-3 bg-gradient-to-r from-slate-100 to-slate-50 rounded-lg border-l-4 border-indigo-500 mt-2">
                      <h3 className="text-sm md:text-base font-bold text-slate-800">📍 {building}</h3>
                    </div>
                    
                    {/* Room rows for this building */}
                    {rooms.map((resource) => (
                      <React.Fragment key={resource}>
                        <div className={`p-3 md:p-4 lg:p-5 rounded-2xl shadow-lg border-2 transition-all duration-300 hover:shadow-xl hover:scale-105 min-h-[100px] md:min-h-[120px] lg:min-h-[140px] ${getResourceColorClasses(resource)}`}>
                          <div className="flex flex-col">
                            <div className="font-bold text-sm md:text-base lg:text-lg mb-2 leading-tight">{resource}</div>
                            <div className="text-xs md:text-sm space-y-1">
                              <div className="font-semibold opacity-90 leading-tight">{RESOURCE_INFO[resource].capacity}</div>
                              <div className="font-medium opacity-75 leading-tight">{RESOURCE_INFO[resource].type}</div>
                            </div>
                          </div>
                        </div>
                        {getCurrentWeekDates().map(date => {
                          const dayBookings = getBookingsForDateAndResource(date, resource);
                          const isToday = isSameDay(date, new Date());
                          const hasAccess = canAccessResource(resource);
                          
                          return (
                            <div 
                              key={`${resource}-${date.toISOString()}`}
                              className={`p-2 md:p-3 lg:p-4 rounded-2xl shadow-md border-2 transition-all duration-300 cursor-pointer min-h-[100px] md:min-h-[120px] lg:min-h-[140px] ${
                                !hasAccess
                                  ? 'bg-slate-100 border-slate-300 opacity-60 cursor-not-allowed'
                                  : isToday 
                                    ? 'bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-400 hover:shadow-xl hover:shadow-emerald-500/20 hover:scale-105' 
                                    : 'bg-white border-slate-200 hover:shadow-xl hover:border-indigo-400 hover:scale-105'
                              }`}
                              onClick={() => hasAccess && handleCalendarBoxClick(resource, date)}
                            >
                              {dayBookings.length === 0 ? (
                                <div className="text-slate-400 text-xs md:text-sm text-center py-2 md:py-4 font-medium leading-tight">
                                  {!hasAccess ? '🔒 Låst' : 'Ledig'}
                                </div>
                              ) : (
                                <div className="space-y-1 md:space-y-2">
                                  {dayBookings.slice(0, expandedCells[`desktop-${resource}-${format(date, 'yyyy-MM-dd')}`] ? undefined : 2).map(booking => (
                                    <div 
                                      key={booking.id}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleBookingClick(booking);
                                      }}
                                      className={`p-2 md:p-3 rounded-lg shadow-sm border cursor-pointer transition-all hover:scale-105 hover:shadow-lg ${resourceColors[resource]}`}
                                    >
                                      <div className="font-semibold text-xs md:text-sm leading-tight mb-1 truncate">{booking.title}</div>
                                      <div className="text-xs font-medium opacity-90 leading-tight">{booking.start_time}</div>
                                    </div>
                                  ))}
                                  {dayBookings.length > 2 && (
                                    <div 
                                      className="text-xs md:text-sm text-indigo-600 text-center font-semibold leading-tight cursor-pointer hover:text-indigo-800 transition-colors py-1 rounded-lg bg-indigo-50 hover:bg-indigo-100"
                                      onClick={(e) => toggleCellExpand(`desktop-${resource}-${format(date, 'yyyy-MM-dd')}`, e)}
                                      data-testid={`expand-cell-desktop-${resource}-${format(date, 'yyyy-MM-dd')}`}
                                    >
                                      {expandedCells[`desktop-${resource}-${format(date, 'yyyy-MM-dd')}`] 
                                        ? 'Vis mindre' 
                                        : `+${dayBookings.length - 2} mere`}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </React.Fragment>
                    ))}
                  </React.Fragment>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
        </div>
      </div>

      {/* Booking Details Modal */}
      <Dialog open={showBookingDetails} onOpenChange={setShowBookingDetails}>
        <DialogContent className="max-w-md mx-2 md:mx-auto">
          <DialogHeader>
            <DialogTitle>Møde Detaljer</DialogTitle>
          </DialogHeader>
          
          {selectedBooking && (
            <div className="space-y-4">
              {/* Meeting Title */}
              <div>
                <Label className="text-sm font-semibold text-slate-600">Titel</Label>
                <p className="text-lg font-semibold mt-1">{selectedBooking.title}</p>
              </div>

              {/* Date and Time */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-semibold text-slate-600">Dato</Label>
                  <p className="mt-1">{format(parseISO(selectedBooking.date), 'EEEE, d. MMMM yyyy', { locale: da })}</p>
                </div>
                <div>
                  <Label className="text-sm font-semibold text-slate-600">Tid</Label>
                  <p className="mt-1">{selectedBooking.start_time} - {selectedBooking.end_time}</p>
                </div>
              </div>

              {/* Room */}
              <div>
                <Label className="text-sm font-semibold text-slate-600">Mødelokale</Label>
                <div className="mt-1">
                  <div className={`inline-block px-3 py-2 rounded-lg border-2 ${
                    selectedBooking.resource === 'Fodbolden' 
                      ? 'bg-gradient-to-r from-emerald-100 to-emerald-200 border-emerald-300 text-emerald-900' 
                      : selectedBooking.resource === 'Trampolinen'
                      ? 'bg-gradient-to-r from-blue-100 to-blue-200 border-blue-300 text-blue-900'
                      : 'bg-gradient-to-r from-orange-100 to-orange-200 border-orange-300 text-orange-900'
                  }`}>
                    <div className="font-semibold">{selectedBooking.resource}</div>
                    <div className="text-xs">
                      <div>{RESOURCE_INFO[selectedBooking.resource]?.capacity}</div>
                      <div>{RESOURCE_INFO[selectedBooking.resource]?.type}</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Creator Information */}
              {selectedBooking.creator_name && (
                <div>
                  <Label className="text-sm font-semibold text-slate-600">Booket af</Label>
                  <div className="mt-1 flex items-center gap-2">
                    <div className="bg-slate-100 px-3 py-2 rounded-lg border">
                      <p className="font-semibold text-slate-900">{selectedBooking.creator_name}</p>
                      {selectedBooking.creator_organization && (
                        <p className="text-sm text-slate-600">{selectedBooking.creator_organization}</p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Notes */}
              {selectedBooking.notes && (
                <div>
                  <Label className="text-sm font-semibold text-slate-600">Noter</Label>
                  <p className="mt-1 text-slate-700 bg-slate-50 p-3 rounded-lg border">{selectedBooking.notes}</p>
                </div>
              )}

              {/* Created Date */}
              <div>
                <Label className="text-sm font-semibold text-slate-600">Oprettet</Label>
                <p className="text-sm text-slate-500 mt-1">
                  {format(new Date(selectedBooking.created_at), 'd. MMMM yyyy \'kl.\' HH:mm', { locale: da })}
                </p>
              </div>

              {/* Information for users about deleting their own bookings */}
              {!isAdmin() && selectedBooking.created_by === user?.id && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-start gap-2">
                  <svg 
                    className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path 
                      strokeLinecap="round" 
                      strokeLinejoin="round" 
                      strokeWidth={2} 
                      d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" 
                    />
                  </svg>
                  <p className="text-sm text-blue-800">
                    <span className="font-semibold">Dit møde:</span> Du kan slette dette møde ved at klikke på "Slet Møde" knappen nedenfor.
                  </p>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4 border-t">
                <Button 
                  variant="outline"
                  onClick={closeBookingDetails}
                  className={isAdmin() || selectedBooking.created_by === user?.id ? "flex-1" : "w-full"}
                >
                  Luk
                </Button>
                
                {/* Show delete button for admins or booking owner */}
                {(isAdmin() || selectedBooking.created_by === user?.id) && (
                  <Button 
                    variant="destructive"
                    onClick={async () => {
                      try {
                        await axios.delete(`${API}/bookings/${selectedBooking.id}`, {
                          headers: {
                            'Authorization': `Bearer ${localStorage.getItem('token')}`
                          }
                        });
                        toast.success('Møde slettet succesfuldt');
                        closeBookingDetails();
                        fetchBookings(); // Refresh bookings
                      } catch (error) {
                        toast.error('Kunne ikke slette mødet');
                      }
                    }}
                    className="flex-1"
                  >
                    Slet Møde
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      </div>
      
      {/* Footer - Hidden for now */}
      {/* 
      <footer className="w-full py-12 px-4 mt-16 bg-gradient-to-br from-slate-800 via-slate-700 to-slate-900 border-t border-slate-600">
        <div className="container-huge max-w-4xl mx-auto text-center">
          
          <div className="space-y-6">
            <div>
              <p className="text-slate-300 text-sm md:text-base font-light tracking-wide uppercase mb-3">
                Hjemmeside leveret af
              </p>
              <a 
                href="https://thingmark.dk" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-block group"
              >
                <h3 className="text-4xl md:text-5xl font-bold text-white group-hover:text-indigo-400 transition-colors duration-300 tracking-tight">
                  thingmark.dk
                </h3>
              </a>
            </div>
            
            <div className="w-24 h-0.5 bg-gradient-to-r from-transparent via-indigo-500 to-transparent mx-auto"></div>
            
            <div>
              <p className="text-indigo-300 text-base md:text-lg font-medium">
                Professionel marketing og webløsninger
              </p>
            </div>
            
            <div className="pt-2">
              <a 
                href="https://thingmark.dk" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3 bg-indigo-600 hover:bg-indigo-500 text-white font-semibold rounded-lg transition-all duration-300 hover:shadow-lg hover:shadow-indigo-500/50 hover:scale-105"
              >
                Besøg thingmark.dk
                <svg 
                  className="w-5 h-5" 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth={2} 
                    d="M17 8l4 4m0 0l-4 4m4-4H3" 
                  />
                </svg>
              </a>
            </div>
          </div>
          
          <div className="mt-10 pt-6 border-t border-slate-600/50">
            <p className="text-slate-400 text-xs md:text-sm">
              © {new Date().getFullYear()} thingmark.dk. Alle rettigheder forbeholdes.
            </p>
          </div>
        </div>
      </footer>
      */}
    </div>
  );
}

// Main App component with authentication wrapper
function App() {
  return (
    <AuthProvider>
      <AuthenticatedApp />
    </AuthProvider>
  );
}

// Authentication check component
function AuthenticatedApp() {
  const { isAuthenticated, loading } = useAuth();
  
  // Check if this is an invitation acceptance URL
  const urlParams = new URLSearchParams(window.location.search);
  const inviteToken = urlParams.get('token');
  
  if (inviteToken && window.location.pathname === '/accept-invite') {
    return <AcceptInviteForm token={inviteToken} />;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="text-slate-600 mt-4">Indlæser...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <LoginForm />;
  }

  return <CalendarApp />;
}

export default App;